import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  addDoc,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, app } from "../firebaseConfig";
import { getFunctions, httpsCallable } from "firebase/functions";

const boxStyle = {
  border: "1px solid #1d4ed8",
  borderRadius: 10,
  padding: 12,
  background: "#f8fafc",
  marginBottom: 14,
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid #cbd5e1",
  marginBottom: 8,
};

const buttonStyle = {
  padding: "8px 12px",
  borderRadius: 8,
  border: "1px solid #1d4ed8",
  background: "#e0e7ff",
  fontWeight: 700,
  cursor: "pointer",
};

const greenButtonStyle = {
  ...buttonStyle,
  border: "1px solid #16a34a",
  background: "#dcfce7",
  color: "#166534",
};

const redButtonStyle = {
  ...buttonStyle,
  border: "1px solid #dc2626",
  background: "#fee2e2",
  color: "#991b1b",
};

const tinyButtonStyle = {
  padding: "3px 7px",
  borderRadius: 6,
  border: "1px solid #94a3b8",
  background: "#f8fafc",
  color: "#0f172a",
  fontSize: 11,
  fontWeight: 700,
  cursor: "pointer",
  lineHeight: 1.2,
};

const LoadingSpinner = ({ size = 16, color = "#ffffff" }) => (
  <span
    aria-hidden="true"
    style={{
      width: size,
      height: size,
      border: `2px solid ${color}55`,
      borderTopColor: color,
      borderRadius: "50%",
      display: "inline-block",
      flexShrink: 0,
      animation: "teamMessagingSpin 0.8s linear infinite",
    }}
  />
);

const sectionToggleStyle = (active = false, alert = false) => ({
  width: "100%",
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #1d4ed8",
  background: alert ? "#fecaca" : active ? "#bfdbfe" : "#e0e7ff",
  color: "#0f172a",
  fontWeight: 800,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
});

const getConversationKey = (msg) => {
  if (msg?.audience === "all") return "team_all";

  const ids = Array.from(
    new Set([msg.authorId, ...(msg.recipientIds || [])].filter(Boolean))
  ).sort();

  return `dm_${ids.join("_")}`;
};

const getConversationType = (msg) => {
  return msg?.audience === "all" ? "team" : "direct";
};

const toDateSafe = (value) => {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  if (value instanceof Date) return value;
  return null;
};

const buildConversationLabel = (msg, currentUserId) => {
  if (msg?.audience === "all") return "Team All";

  const otherNames = [];

  if (msg.authorId && msg.authorId !== currentUserId && msg.authorName) {
    otherNames.push(msg.authorName);
  }

  (msg.recipientNames || []).forEach((name, idx) => {
    const rid = msg.recipientIds?.[idx];
    if (rid && rid !== currentUserId && !otherNames.includes(name)) {
      otherNames.push(name);
    }
  });

  if (otherNames.length === 0) return "Direct Message";
  if (otherNames.length === 1) return otherNames[0];
  return `Group: ${otherNames.join(", ")}`;
};

const buildDirectConversationKeyForUsers = (userA, userB) => {
  return `dm_${[userA, userB].filter(Boolean).sort().join("_")}`;
};

const buildDirectConversationLabelForUser = (user) => {
  return user?.name || "Direct Message";
};

const TeamMessaging = ({
  userId,
  userFullName,
  isOpen,
  onViewed,
  openTaskCount = 0,
  usersList = [],
}) => {
  const [messages, setMessages] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDetails, setNewTaskDetails] = useState("");
  const [loading, setLoading] = useState(true);
  
  const [showAddTask, setShowAddTask] = useState(false);
  const [showOpenTasks, setShowOpenTasks] = useState(false);
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);

  const [showDirectMessage, setShowDirectMessage] = useState(false);
  const [messageSearch, setMessageSearch] = useState("");

const [selectedMessageFiles, setSelectedMessageFiles] = useState([]);
const [sendingMessage, setSendingMessage] = useState(false);
const [replyingToMessage, setReplyingToMessage] = useState(null);

const [activeConversationKey, setActiveConversationKey] = useState("team_all");
const [conversationSeenMap, setConversationSeenMap] = useState({});
const [showNewGroupPicker, setShowNewGroupPicker] = useState(false);
const [groupDraftRecipients, setGroupDraftRecipients] = useState([]);

const [selectedTaskFiles, setSelectedTaskFiles] = useState([]);
const [addingTask, setAddingTask] = useState(false);

const [newTaskUrgent, setNewTaskUrgent] = useState(false);
const [hiddenConversationKeys, setHiddenConversationKeys] = useState({});

const [newTaskAssignedIds, setNewTaskAssignedIds] = useState(["ALL_TEAM"]);

const functions = getFunctions(app, "us-central1");
const sendTeamMessageEmailCallable = httpsCallable(functions, "sendTeamMessageEmail");
const sendNotesTaskEmailCallable = httpsCallable(functions, "sendNotesTaskEmail");

const activityRef = useRef(null);
useEffect(() => {
  if (!userId) return;

  const seenRef = doc(db, "teamMessagingSeen", userId);

  const unsub = onSnapshot(seenRef, (snap) => {
    const data = snap.exists() ? snap.data() : {};
    setConversationSeenMap(data?.conversationSeen || {});
    setHiddenConversationKeys(data?.hiddenConversations || {});
  });

  return () => unsub();
}, [userId]);

  useEffect(() => {
    const msgQuery = query(
      collection(db, "teamMessages"),
      orderBy("createdAt", "desc")
    );

    const taskQuery = query(
      collection(db, "teamTasks"),
      orderBy("createdAt", "desc")
    );

    const unsubMessages = onSnapshot(msgQuery, (snapshot) => {
      const data = snapshot.docs
  .map((d) => ({
    id: d.id,
    ...d.data(),
  }))
  .filter((msg) => {
    if (msg.audience === "all") return true;
    if (msg.authorId === userId) return true;
    return Array.isArray(msg.recipientIds) && msg.recipientIds.includes(userId);
  });

setMessages(data);
      setLoading(false);
    });

    const unsubTasks = onSnapshot(taskQuery, (snapshot) => {
      const data = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      setTasks(data);
    });

    return () => {
      unsubMessages();
      unsubTasks();
    };
  }, [userId]);

  useEffect(() => {
    const markViewed = async () => {
      if (!isOpen || !userId || !activeConversationKey) return;
  
      const seenRef = doc(db, "teamMessagingSeen", userId);
  
      await setDoc(
        seenRef,
        {
          userId,
          userName: userFullName || "User",
          lastSeenAt: serverTimestamp(),
          conversationSeen: {
            [activeConversationKey]: new Date(),
          },
        },
        { merge: true }
      );
    };
  
    markViewed();
  }, [isOpen, userId, userFullName, activeConversationKey]);

  const userMap = useMemo(() => {
    const map = {};
    usersList.forEach((u) => {
      map[u.id] = u;
    });
    return map;
  }, [usersList]);

  const isAdminUser = userMap[userId]?.role === "Admin";

  const availableRecipients = useMemo(() => {
    return usersList
    .filter((u) => u.id !== userId)
    .filter((u) => u.hideFromTeamMessaging !== true)
    .filter((u) => ["Admin", "Manager", "Assistant", "Tech"].includes(u.role))
      .map((u) => ({
        id: u.id,
        name: `${u.firstName || ""} ${u.lastName || ""}`.trim(),
        role: u.role || "",
      }))
      .filter((u) => u.name);
  }, [usersList, userId]);

  const isMessageVisibleToMe = useCallback((msg) => {
    if (msg?.audience === "all") return true;
    if (msg?.authorId === userId) return true;
    return Array.isArray(msg?.recipientIds) && msg.recipientIds.includes(userId);
  }, [userId]);

  const conversations = useMemo(() => {
    const map = new Map();
  
    map.set("team_all", {
      key: "team_all",
      label: "Team All",
      type: "team",
      latestAt: null,
      unread: false,
      recipientIds: [],
      recipientNames: ["Team"],
    });
  
    // Existing conversations from messages
    messages.forEach((msg) => {
      if (!isMessageVisibleToMe(msg)) return;
  
      const key = getConversationKey(msg);
      const label = buildConversationLabel(msg, userId);
      const createdAt = toDateSafe(msg.createdAt);
  
      const existing = map.get(key);
  
      if (!existing) {
        map.set(key, {
          key,
          label,
          type: getConversationType(msg),
          latestAt: createdAt,
          unread: false,
          recipientIds: msg.audience === "all"
            ? []
            : Array.from(new Set([msg.authorId, ...(msg.recipientIds || [])].filter((id) => id && id !== userId))),
          recipientNames:
            msg.audience === "all"
              ? ["Team"]
              : Array.from(
                  new Set(
                    [
                      msg.authorId !== userId ? msg.authorName : null,
                      ...(msg.recipientNames || []).filter(Boolean),
                    ].filter(Boolean)
                  )
                ).filter((name) => name !== userFullName),
        });
      } else if (createdAt && (!existing.latestAt || createdAt > existing.latestAt)) {
        existing.latestAt = createdAt;
      }
    });
  
    // Add every available user as a direct-message option, even if no thread exists yet
    availableRecipients.forEach((user) => {
      const key = buildDirectConversationKeyForUsers(userId, user.id);
  
      if (!map.has(key)) {
        map.set(key, {
          key,
          label: buildDirectConversationLabelForUser(user),
          type: "direct",
          latestAt: null,
          unread: false,
          recipientIds: [user.id],
          recipientNames: [user.name],
        });
      }
    });
  
    const list = Array.from(map.values()).map((conv) => {
      const seenAt = toDateSafe(conversationSeenMap?.[conv.key]);
      const latestIncoming = messages
        .filter((msg) => getConversationKey(msg) === conv.key)
        .filter((msg) => msg.authorId !== userId)
        .map((msg) => toDateSafe(msg.createdAt))
        .filter(Boolean)
        .sort((a, b) => b - a)[0] || null;
  
      return {
        ...conv,
        unread: !!latestIncoming && (!seenAt || latestIncoming > seenAt),
      };
    });
  
    const visibleList = list.filter((conv) => {
      if (conv.key === "team_all") return true;
      return !hiddenConversationKeys?.[conv.key];
    });
    
    return visibleList.sort((a, b) => {
      if (a.key === "team_all") return -1;
      if (b.key === "team_all") return 1;
      return (b.latestAt?.getTime?.() || 0) - (a.latestAt?.getTime?.() || 0);
    });
  }, [messages, userId, userFullName, conversationSeenMap, availableRecipients, hiddenConversationKeys, isMessageVisibleToMe]);

  const filteredMessages = useMemo(() => {
    return messages.filter((msg) => getConversationKey(msg) === activeConversationKey);
  }, [messages, activeConversationKey]);
  
  const visibleMessages = useMemo(() => {
    const term = messageSearch.trim().toLowerCase();
  
    if (!term) return filteredMessages;
  
    return filteredMessages.filter((msg) => {
      const textMatch = (msg.text || "").toLowerCase().includes(term);
      const authorMatch = (msg.authorName || "").toLowerCase().includes(term);
      const recipientMatch = (msg.recipientNames || []).some((name) =>
        (name || "").toLowerCase().includes(term)
      );
      const fileMatch = (msg.files || []).some((file) =>
        (file.fileName || "").toLowerCase().includes(term)
      );
  
      return textMatch || authorMatch || recipientMatch || fileMatch;
    });
  }, [filteredMessages, messageSearch]);

  const activeConversation = useMemo(() => {
    return conversations.find((c) => c.key === activeConversationKey) || {
      key: "team_all",
      label: "Team All",
      type: "team",
      recipientIds: [],
      recipientNames: ["Team"],
    };
  }, [conversations, activeConversationKey]);
  
  const hiddenConversationsList = useMemo(() => {
    return Object.entries(hiddenConversationKeys || {})
      .filter(([, hidden]) => hidden)
      .map(([key]) => {
        const found = messages.find((msg) => getConversationKey(msg) === key);
  
        if (found) {
          return {
            key,
            label: buildConversationLabel(found, userId),
          };
        }
  
        const recipient = availableRecipients.find(
          (u) => buildDirectConversationKeyForUsers(userId, u.id) === key
        );
  
        return {
          key,
          label: recipient?.name || "Hidden Conversation",
        };
      });
  }, [hiddenConversationKeys, messages, userId, availableRecipients]);

  useEffect(() => {
    if (!activityRef.current) return;
    activityRef.current.scrollTop = 0;
  }, [visibleMessages, isOpen]);

  const handleMessageFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedMessageFiles(files);
  };

  const handleTaskFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedTaskFiles(files);
  };
  
  const uploadTaskFiles = async (files) => {
    if (!files.length) return [];
  
    const storage = getStorage();
  
    const uploads = await Promise.all(
      files.map(async (file) => {
        const safeName = `${Date.now()}_${file.name.replace(/\s+/g, "_")}`;
        const storageRef = ref(storage, `teamTasks/${userId}/${safeName}`);
  
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
  
        return {
          fileName: file.name,
          fileURL: url,
          contentType: file.type || "",
        };
      })
    );
  
    return uploads;
  };

  const uploadMessageFiles = async (files) => {
    if (!files.length) return [];
  
    const storage = getStorage();
  
    const uploads = await Promise.all(
      files.map(async (file) => {
        const safeName = `${Date.now()}_${file.name.replace(/\s+/g, "_")}`;
        const storageRef = ref(storage, `teamMessages/${userId}/${safeName}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
  
        return {
          fileName: file.name,
          fileURL: url,
          contentType: file.type || "",
        };
      })
    );
  
    return uploads;
  };
  
  const formatRecipientsLabel = (msg) => {
    if (msg?.audience === "all") return "Team";
    if (Array.isArray(msg?.recipientNames) && msg.recipientNames.length > 0) {
      return msg.recipientNames.join(", ");
    }
    return "Selected Users";
  };
  
  const renderMessageText = (text = "") => {
    const parts = text.split(/(\s+)/);
  
    return parts.map((part, idx) => {
      if (/^#\w+/i.test(part)) {
        return (
          <span key={idx} style={{ color: "#1d4ed8", fontWeight: 700 }}>
            {part}
          </span>
        );
      }
      return <React.Fragment key={idx}>{part}</React.Fragment>;
    });
  };

  const startReplyToMessage = (msg) => {
    if (!msg?.id) return;

    setReplyingToMessage({
      id: msg.id,
      text: msg.text || "",
      authorId: msg.authorId || "",
      authorName: msg.authorName || "User",
      createdAt: msg.createdAt || null,
      files: Array.isArray(msg.files) ? msg.files : [],
    });
  };

  const cancelReplyToMessage = () => {
    setReplyingToMessage(null);
  };

  const getReplyPreviewText = (reply) => {
    if (!reply) return "";

    const text = (reply.text || "").trim();
    if (text) return text;

    if (Array.isArray(reply.files) && reply.files.length > 0) {
      return reply.files.length === 1
        ? `Attachment: ${reply.files[0]?.fileName || "file"}`
        : `${reply.files.length} attachments`;
    }

    return "Original message";
  };

  const toggleGroupDraftRecipient = (recipientId) => {
    setGroupDraftRecipients((prev) =>
      prev.includes(recipientId)
        ? prev.filter((id) => id !== recipientId)
        : [...prev, recipientId]
    );
  };

  const startGroupConversation = () => {
    const ids = Array.from(
      new Set([userId, ...groupDraftRecipients].filter(Boolean))
    ).sort();
  
    if (ids.length <= 2) {
      alert("Please choose at least 2 other users for a group message.");
      return;
    }
  
    const key = `dm_${ids.join("_")}`;
  
    setActiveConversationKey(key);
  
    setConversationSeenMap((prev) => ({
      ...prev,
      [key]: new Date(),
    }));
  
    setShowNewGroupPicker(false);
    setGroupDraftRecipients([]);
  };

  const sendMessage = async () => {
    const trimmed = newMessage.trim();
  
    if (sendingMessage) return;
    if (!userId) return;
    if (!trimmed && selectedMessageFiles.length === 0) return;
  
    setSendingMessage(true);
  
    try {
      const uploadedFiles = await uploadMessageFiles(selectedMessageFiles);
  
      const isTeamAll = activeConversationKey === "team_all";
  
      const payload = {
        text: trimmed,
        authorId: userId,
        authorName: userFullName || "User",
        createdAt: serverTimestamp(),
        type: "message",
        audience: isTeamAll ? "all" : "selected",
        recipientIds: isTeamAll ? [] : (activeConversation.recipientIds || []),
        recipientNames: isTeamAll ? ["Team"] : (activeConversation.recipientNames || []),
        files: uploadedFiles,
        ...(replyingToMessage
          ? {
              replyTo: {
                messageId: replyingToMessage.id,
                authorId: replyingToMessage.authorId || "",
                authorName: replyingToMessage.authorName || "User",
                text: replyingToMessage.text || "",
                files: Array.isArray(replyingToMessage.files)
                  ? replyingToMessage.files.map((file) => ({
                      fileName: file?.fileName || "",
                      fileURL: file?.fileURL || "",
                      contentType: file?.contentType || "",
                    }))
                  : [],
              },
            }
          : {}),
      };
  
      const messageDocRef = await addDoc(collection(db, "teamMessages"), payload);

      try {
        await sendTeamMessageEmailCallable({
          messageId: messageDocRef.id,
        });
      } catch (emailErr) {
        console.error("Message saved, but email notification failed:", emailErr);
      }
  
      setConversationSeenMap((prev) => ({
        ...prev,
        [activeConversationKey]: new Date(),
      }));
  
      setNewMessage("");
      setSelectedMessageFiles([]);
      setReplyingToMessage(null);
    } catch (err) {
      console.error("Error sending message:", err);
      alert("Could not send message.");
    } finally {
      setSendingMessage(false);
    }
  };

  const toggleLikeMessage = async (msg) => {
    if (!userId || !msg?.id) return;
  
    try {
      const msgRef = doc(db, "teamMessages", msg.id);
      const likedBy = Array.isArray(msg.likedBy) ? msg.likedBy : [];
      const likedByNames = Array.isArray(msg.likedByNames) ? msg.likedByNames : [];
      const alreadyLiked = likedBy.includes(userId);
  
      if (alreadyLiked) {
        await updateDoc(msgRef, {
          likedBy: likedBy.filter((id) => id !== userId),
          likedByNames: likedByNames.filter((name) => name !== (userFullName || "User")),
        });
      } else {
        await updateDoc(msgRef, {
          likedBy: [...likedBy, userId],
          likedByNames: [...likedByNames, userFullName || "User"],
        });
      }
    } catch (err) {
      console.error("Error toggling like:", err);
      alert("Could not update like.");
    }
  };
  
  const hideConversation = async (convKey) => {
    if (!userId || !convKey) return;
  
    try {
      const seenRef = doc(db, "teamMessagingSeen", userId);
  
      await setDoc(
        seenRef,
        {
          hiddenConversations: {
            [convKey]: true,
          },
        },
        { merge: true }
      );
  
      if (activeConversationKey === convKey) {
        setActiveConversationKey("team_all");
      }
    } catch (err) {
      console.error("Error hiding conversation:", err);
      alert("Could not hide conversation.");
    }
  };
  
  const unhideConversation = async (convKey) => {
    if (!userId || !convKey) return;
  
    try {
      const seenRef = doc(db, "teamMessagingSeen", userId);
  
      await setDoc(
        seenRef,
        {
          hiddenConversations: {
            [convKey]: false,
          },
        },
        { merge: true }
      );
    } catch (err) {
      console.error("Error unhiding conversation:", err);
      alert("Could not unhide conversation.");
    }
  };
  
  const getTaskAssignedLabel = (task) => {
    if (Array.isArray(task.assignedToNames) && task.assignedToNames.length > 0) {
      return task.assignedToNames.join(", ");
    }
  
    if (Array.isArray(task.assignedToIds) && task.assignedToIds.includes("ALL_TEAM")) {
      return "Entire Team";
    }
  
    return "Unassigned";
  };

  const addTask = async () => {
  if (addingTask) return;
  if (!newTaskTitle.trim() || !userId) return;

  setAddingTask(true);

  try {
    const uploadedFiles = await uploadTaskFiles(selectedTaskFiles);

    const taskDocRef = await addDoc(collection(db, "teamTasks"), {
      title: newTaskTitle.trim(),
      details: newTaskDetails.trim(),
      status: "Open",
      urgent: newTaskUrgent,
      color: newTaskUrgent ? "#fee2e2" : "#dbeafe",
      createdAt: serverTimestamp(),
      createdById: userId,
      createdByName: userFullName || "User",
    
      assignedToIds: newTaskAssignedIds.includes("ALL_TEAM")
        ? ["ALL_TEAM"]
        : newTaskAssignedIds,
    
      assignedToNames: newTaskAssignedIds.includes("ALL_TEAM")
        ? ["Entire Team"]
        : availableRecipients
            .filter((u) => newTaskAssignedIds.includes(u.id))
            .map((u) => u.name),
    
      visibility: newTaskAssignedIds.includes("ALL_TEAM")
        ? "team"
        : "assigned",
    
      completedAt: null,
      completedById: null,
      completedByName: "",
      files: uploadedFiles,
      showOnNotesTasksPage: true,
      source: "TeamMessaging",
    });
    
    await addDoc(collection(db, "teamMessages"), {
      text: `created a task: ${newTaskTitle.trim()}`,
      authorId: userId,
      authorName: userFullName || "User",
      createdAt: serverTimestamp(),
      type: "task-created",
      audience: "all",
      recipientIds: [],
      recipientNames: ["Team"],
    });
    
    try {
      await sendNotesTaskEmailCallable({
        taskId: taskDocRef.id,
      });
    } catch (emailErr) {
      console.error("Task saved, but Notes / Tasks email failed:", emailErr);
    }

    await addDoc(collection(db, "teamMessages"), {
      text: `created a task: ${newTaskTitle.trim()}`,
      authorId: userId,
      authorName: userFullName || "User",
      createdAt: serverTimestamp(),
      type: "task-created",
    });

    setNewTaskTitle("");
    setNewTaskDetails("");
    setSelectedTaskFiles([]);
    setNewTaskUrgent(false);
    setNewTaskAssignedIds(["ALL_TEAM"]);

  } catch (err) {
    console.error(err);
    alert("Error creating task");
  } finally {
    setAddingTask(false);
  }
};

  const completeTask = async (task) => {
    if (!userId) return;

    const taskRef = doc(db, "teamTasks", task.id);

    await updateDoc(taskRef, {
      status: "Completed",
      completedAt: serverTimestamp(),
      completedById: userId,
      completedByName: userFullName || "User",
    });

    await addDoc(collection(db, "teamMessages"), {
      text: `completed task: ${task.title}`,
      authorId: userId,
      authorName: userFullName || "User",
      createdAt: serverTimestamp(),
      type: "task-completed",
    });
  };

  const deleteAllCompletedTasks = async () => {
    if (completedTasks.length === 0) return;
  
    const ok = window.confirm(
      `Delete all ${completedTasks.length} completed task(s)? This cannot be undone.`
    );
  
    if (!ok) return;
  
    try {
      const storage = getStorage();
  
      await Promise.all(
        completedTasks.map(async (task) => {
          if (Array.isArray(task.files)) {
            await Promise.all(
              task.files.map(async (file) => {
                if (!file?.fileURL) return;
                try {
                  await deleteObject(ref(storage, file.fileURL));
                } catch (fileErr) {
                  console.warn("Could not delete completed task file:", fileErr);
                }
              })
            );
          }
  
          await deleteDoc(doc(db, "teamTasks", task.id));
        })
      );
  
      await addDoc(collection(db, "teamMessages"), {
        text: `deleted all completed tasks`,
        authorId: userId,
        authorName: userFullName || "User",
        createdAt: serverTimestamp(),
        type: "tasks-deleted",
        audience: "all",
        recipientIds: [],
        recipientNames: ["Team"],
      });
    } catch (err) {
      console.error("Error deleting completed tasks:", err);
      alert("Could not delete all completed tasks.");
    }
  };

  const deleteTask = async (task) => {
    if (!task?.id) return;
  
    const ok = window.confirm(`Delete task "${task.title}"?`);
    if (!ok) return;
  
    try {
      const storage = getStorage();
  
      if (Array.isArray(task.files) && task.files.length > 0) {
        await Promise.all(
          task.files.map(async (file) => {
            if (!file?.fileURL) return;
            try {
              const fileRef = ref(storage, file.fileURL);
              await deleteObject(fileRef);
            } catch (fileErr) {
              console.warn("Could not delete file:", fileErr);
            }
          })
        );
      }
  
      await deleteDoc(doc(db, "teamTasks", task.id));
  
      await addDoc(collection(db, "teamMessages"), {
        text: `deleted task: ${task.title}`,
        authorId: userId,
        authorName: userFullName || "User",
        createdAt: serverTimestamp(),
        type: "task-deleted",
        audience: "all",
        recipientIds: [],
        recipientNames: ["Team"],
      });
    } catch (err) {
      console.error("Error deleting task:", err);
      alert("Could not delete task.");
    }
  };

  const reopenTask = async (task) => {
    const taskRef = doc(db, "teamTasks", task.id);

    await updateDoc(taskRef, {
      status: "Open",
      completedAt: null,
      completedById: null,
      completedByName: "",
    });

    await addDoc(collection(db, "teamMessages"), {
      text: `re-opened task: ${task.title}`,
      authorId: userId,
      authorName: userFullName || "User",
      createdAt: serverTimestamp(),
      type: "task-reopened",
    });
  };

  const markTaskUrgent = async (task) => {
    if (!task?.id) return;
  
    try {
      await updateDoc(doc(db, "teamTasks", task.id), {
        urgent: true,
      });
    } catch (err) {
      console.error("Error marking task urgent:", err);
      alert("Could not mark task urgent.");
    }
  };
  
  const markTaskNonUrgent = async (task) => {
    if (!task?.id) return;
  
    try {
      await updateDoc(doc(db, "teamTasks", task.id), {
        urgent: false,
      });
    } catch (err) {
      console.error("Error marking task non-urgent:", err);
      alert("Could not mark task non-urgent.");
    }
  };

  const visibleTasks = useMemo(() => {
    return tasks.filter((task) => {
      if (isAdminUser) return true;
      if (task.createdById === userId) return true;
      return (
        Array.isArray(task.assignedToIds) &&
        (
          task.assignedToIds.includes("ALL_TEAM") ||
          task.assignedToIds.includes(userId)
        )
      );
    });
  }, [tasks, isAdminUser, userId]);
  
  const openTasks = useMemo(() => {
    return visibleTasks
      .filter((t) => t.status !== "Completed")
      .sort((a, b) => {
        const urgentA = a.urgent ? 1 : 0;
        const urgentB = b.urgent ? 1 : 0;
  
        if (urgentA !== urgentB) return urgentB - urgentA;
  
        const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
        const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
        return bTime - aTime;
      });
  }, [visibleTasks]);
  
  const completedTasks = useMemo(
    () => visibleTasks.filter((t) => t.status === "Completed"),
    [visibleTasks]
  );

  if (!isOpen) return null;

  return (
    <div
      style={{
        border: "1px solid #1d4ed8",
        borderRadius: 10,
        padding: 12,
        marginBottom: 14,
        background: "#ffffff",
      }}
    >
      <style>{`
        @keyframes teamMessagingSpin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <h3 style={{ marginTop: 0, marginBottom: 12 }}>💬 Team Messaging</h3>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: window.innerWidth < 900 ? "1fr" : "1.2fr 0.8fr",
          gap: 14,
          alignItems: "start",
        }}
      >
        {/* LEFT SIDE */}
        <div>
        <div style={boxStyle}>
  <h4 style={{ marginTop: 0 }}>New Message</h4>

  <div
  style={{
    marginBottom: 8,
    fontSize: 13,
    fontWeight: 700,
    color: "#1d4ed8",
  }}
>
  Sending to: {activeConversation?.label || "Team All"}
</div>

  {replyingToMessage && (
    <div
      style={{
        marginBottom: 8,
        padding: "9px 10px",
        borderRadius: 8,
        background: "#e5e7eb",
        borderLeft: "4px solid #64748b",
        color: "#475569",
        position: "relative",
      }}
    >
      <button
        type="button"
        onClick={cancelReplyToMessage}
        title="Cancel reply"
        style={{
          ...tinyButtonStyle,
          position: "absolute",
          top: 6,
          right: 6,
          padding: "2px 6px",
          background: "#f8fafc",
        }}
      >
        ✕
      </button>

      <div style={{ fontSize: 11, fontWeight: 800, marginBottom: 3 }}>
        Replying to {replyingToMessage.authorName || "User"}
      </div>

      <div
        style={{
          fontSize: 12,
          paddingRight: 28,
          whiteSpace: "pre-wrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
        }}
      >
        {getReplyPreviewText(replyingToMessage)}
      </div>
    </div>
  )}

  <textarea
    value={newMessage}
    onChange={(e) => setNewMessage(e.target.value)}
    placeholder={
      activeConversationKey === "team_all"
        ? "Type a message to the team... emojis and #tags work here"
        : `Type a message to ${activeConversation?.label || "this user"}... emojis and #tags work here`
    }
    rows={4}
    style={inputStyle}
  />

  <input
    type="file"
    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
    multiple
    onChange={handleMessageFileChange}
    style={{ marginBottom: 8 }}
  />

  {selectedMessageFiles.length > 0 && (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>Selected Files</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {selectedMessageFiles.map((file, idx) => {
          const isImage = file.type?.startsWith("image/");
          return (
            <div
              key={`${file.name}-${idx}`}
              style={{
                border: "1px solid #cbd5e1",
                borderRadius: 8,
                padding: 6,
                background: "#fff",
              }}
            >
              {isImage ? (
                <img
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  style={{
                    width: 70,
                    height: 70,
                    objectFit: "cover",
                    borderRadius: 6,
                    display: "block",
                    marginBottom: 4,
                  }}
                />
              ) : null}
              <div style={{ fontSize: 12, maxWidth: 120, wordBreak: "break-word" }}>
                {file.name}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  )}

<button
  onClick={sendMessage}
  style={{
    ...buttonStyle,
    background: "#1e3a8a",
    border: "1px solid #1e40af",
    color: "#fff",
    width: "100%",
    padding: "11px 14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    opacity: sendingMessage ? 0.75 : 1,
    cursor: sendingMessage ? "not-allowed" : "pointer",
  }}
  disabled={sendingMessage}
>
  {sendingMessage ? (
    <>
      <LoadingSpinner />
      <span>Sending...</span>
    </>
  ) : (
    "Send Message"
  )}
</button>
</div>

<div style={boxStyle}>
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 10,
      flexWrap: "wrap",
      marginBottom: 8,
    }}
  >
    <h4 style={{ marginTop: 0, marginBottom: 0 }}>
      Team Activity:{" "}
      {conversations.find((c) => c.key === activeConversationKey)?.label || "Team All"}
    </h4>

    <input
      type="text"
      value={messageSearch}
      onChange={(e) => setMessageSearch(e.target.value)}
      placeholder="Search this conversation..."
      style={{
        padding: "7px 10px",
        borderRadius: 8,
        border: "1px solid #cbd5e1",
        minWidth: 220,
        maxWidth: 260,
        fontSize: 12,
      }}
    />
  </div>

    {loading ? (
      <p>Loading…</p>
    ) : visibleMessages.length === 0 ? (
      <p style={{ color: "#64748b" }}>
        {messageSearch.trim()
          ? "No messages match your search in this conversation."
          : "No messages in this conversation yet."}
      </p>
    ) : (
      <div ref={activityRef} style={{ maxHeight: 420, overflowY: "auto" }}>
        {visibleMessages.map((msg) => (
        <div
          key={msg.id}
          style={{
            borderBottom: "1px solid #e2e8f0",
            padding: "10px 0",
          }}
        >
         <div
  style={{
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
    alignItems: "center",
  }}
>
  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    {userMap[msg.authorId]?.avatarURL ? (
      <img
        src={userMap[msg.authorId].avatarURL}
        alt={msg.authorName || "User"}
        style={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          objectFit: "cover",
          border: "1px solid #cbd5e1",
        }}
      />
    ) : (
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: "#dbeafe",
          border: "1px solid #93c5fd",
        }}
      />
    )}

    <div style={{ fontWeight: 700 }}>
      {msg.authorName || "User"}
    </div>
  </div>

  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap",
      justifyContent: "flex-end",
    }}
  >
    <div style={{ fontSize: 12, color: "#64748b" }}>
      {msg.createdAt?.toDate ? msg.createdAt.toDate().toLocaleString() : ""}
    </div>

    <button
      type="button"
      onClick={() => startReplyToMessage(msg)}
      style={{
        ...tinyButtonStyle,
        background: "#f1f5f9",
        border: "1px solid #94a3b8",
        color: "#0f172a",
      }}
      title="Reply to this specific message"
    >
      ↩ Reply
    </button>

    <button
      type="button"
      onClick={() => toggleLikeMessage(msg)}
      style={{
        ...tinyButtonStyle,
        background: Array.isArray(msg.likedBy) && msg.likedBy.includes(userId)
          ? "#dbeafe"
          : "#f8fafc",
        border: Array.isArray(msg.likedBy) && msg.likedBy.includes(userId)
          ? "1px solid #2563eb"
          : "1px solid #94a3b8",
        color: "#0f172a",
      }}
      title="Like message"
    >
      👍 {Array.isArray(msg.likedBy) ? msg.likedBy.length : 0}
    </button>
  </div>
</div>

          <div style={{ fontSize: 12, color: "#1d4ed8", fontWeight: 700, marginTop: 2 }}>
            To: {formatRecipientsLabel(msg)}
          </div>

          {msg.replyTo && (
            <div
              style={{
                marginTop: 7,
                padding: "8px 10px",
                borderRadius: 8,
                background: "#e5e7eb",
                borderLeft: "4px solid #94a3b8",
                color: "#64748b",
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 800, marginBottom: 3 }}>
                {msg.replyTo.authorName || "User"}
              </div>

              <div
                style={{
                  fontSize: 12,
                  whiteSpace: "pre-wrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {getReplyPreviewText(msg.replyTo)}
              </div>
            </div>
          )}

          {msg.text ? (
            <div style={{ color: "#334155", marginTop: 6, whiteSpace: "pre-wrap" }}>
              {renderMessageText(msg.text)}
            </div>
          ) : null}

          {Array.isArray(msg.files) && msg.files.length > 0 && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
              {msg.files.map((file, idx) => {
                const isImage =
                  file?.contentType?.startsWith("image/") ||
                  /\.(jpg|jpeg|png|gif|webp)$/i.test(file.fileURL || "");

                return (
                  <div
                    key={`${file.fileURL}-${idx}`}
                    style={{
                      border: "1px solid #cbd5e1",
                      borderRadius: 8,
                      padding: 6,
                      background: "#fff",
                      maxWidth: 130,
                    }}
                  >
                    {isImage ? (
                      <a href={file.fileURL} target="_blank" rel="noopener noreferrer">
                        <img
                          src={file.fileURL}
                          alt={file.fileName || `file-${idx}`}
                          style={{
                            width: 100,
                            height: 100,
                            objectFit: "cover",
                            borderRadius: 6,
                            display: "block",
                            marginBottom: 4,
                          }}
                        />
                      </a>
                    ) : (
                      <a
                        href={file.fileURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontWeight: 700 }}
                      >
                        {file.fileName || `Attachment ${idx + 1}`}
                      </a>
                    )}

                    <div style={{ fontSize: 11, wordBreak: "break-word" }}>
                      {file.fileName}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        {Array.isArray(msg.likedByNames) && msg.likedByNames.length > 0 && (
  <div style={{ marginTop: 6, fontSize: 11, color: "#64748b" }}>
    Liked by: {msg.likedByNames.join(", ")}
  </div>
)}
        </div>
      ))}
    </div>
  )}
</div>
        </div>

        {/* RIGHT SIDE */}
        <div>
          <div style={{ ...boxStyle, padding: 10 }}>
            <button
              type="button"
              onClick={() => setShowAddTask((p) => !p)}
              style={sectionToggleStyle(showAddTask)}
            >
              <span>{showAddTask ? "Hide Add Task" : "Show Add Task"}</span>
              <span>{showAddTask ? "▲" : "▼"}</span>
            </button>

            {showAddTask && (
              <div style={{ marginTop: 10 }}>
                <input
                  type="text"
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  placeholder="Task title"
                  style={inputStyle}
                />
                <textarea
                  value={newTaskDetails}
                  onChange={(e) => setNewTaskDetails(e.target.value)}
                  placeholder="Task details"
                  rows={3}
                  style={inputStyle}
                />

                <input
  type="file"
  accept="image/*"
  onChange={handleTaskFileChange}
  style={{ marginBottom: 8 }}
/>

{selectedTaskFiles.length > 0 && (
  <div style={{ marginBottom: 8 }}>
    {selectedTaskFiles.map((file, i) => (
      <img
        key={i}
        src={URL.createObjectURL(file)}
        alt=""
        style={{
          width: 60,
          height: 60,
          objectFit: "cover",
          borderRadius: 6,
          marginRight: 6
        }}
      />
    ))}
  </div>
)}
<div style={{ marginBottom: 10 }}>
  <button
    type="button"
    onClick={() => setNewTaskUrgent((prev) => !prev)}
    style={{
      ...tinyButtonStyle,
      background: newTaskUrgent ? "#fee2e2" : "#f8fafc",
      border: newTaskUrgent ? "1px solid #dc2626" : "1px solid #94a3b8",
      color: newTaskUrgent ? "#991b1b" : "#0f172a",
      fontSize: 12,
      padding: "6px 10px",
    }}
  >
    {newTaskUrgent ? "Urgent ✓" : "Mark Urgent"}
  </button>
</div>
               <button
  onClick={addTask}
  disabled={addingTask}
  style={{
    ...greenButtonStyle,
    marginBottom: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    opacity: addingTask ? 0.75 : 1,
    cursor: addingTask ? "not-allowed" : "pointer",
  }}
>
  {addingTask ? (
    <>
      <LoadingSpinner color="#166534" />
      <span>Adding Task...</span>
    </>
  ) : (
    "Add Task"
  )}
</button>
              
          

          <div style={{ marginBottom: 10 }}>
  <div style={{ fontWeight: 800, marginBottom: 6 }}>
    Assign Task To:
  </div>

  {/* TEAM OPTION */}
  <label
    style={{
      display: "block",
      marginBottom: 8,
      padding: 8,
      borderRadius: 8,
      background: "#dbeafe",
      border: "1px solid #93c5fd",
      fontWeight: 700,
    }}
  >
    <input
      type="checkbox"
      checked={newTaskAssignedIds.includes("ALL_TEAM")}
      onChange={() => {
        setNewTaskAssignedIds((prev) =>
          prev.includes("ALL_TEAM") ? [] : ["ALL_TEAM"]
        );
      }}
      style={{ marginRight: 8 }}
    />
    Entire Team
  </label>

  {/* INDIVIDUAL USERS */}
  {!newTaskAssignedIds.includes("ALL_TEAM") &&
    availableRecipients.map((user) => (
      <label
        key={user.id}
        style={{
          display: "block",
          marginBottom: 5,
        }}
      >
        <input
          type="checkbox"
          checked={newTaskAssignedIds.includes(user.id)}
          onChange={() =>
            setNewTaskAssignedIds((prev) =>
              prev.includes(user.id)
                ? prev.filter((id) => id !== user.id)
                : [...prev, user.id]
            )
          }
          style={{ marginRight: 8 }}
        />
        {user.name} {user.role ? `(${user.role})` : ""}
      </label>
    ))}
</div>
</div>
            )}
</div>
          <div style={{ ...boxStyle, padding: 10 }}>
            <button
              type="button"
              onClick={() => setShowOpenTasks((p) => !p)}
              style={sectionToggleStyle(showOpenTasks, openTasks.length > 0)}
            >
              <span>
                {showOpenTasks ? "Hide Open Tasks" : "Show Open Tasks"} ({openTasks.length})
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {openTasks.length > 0 && (
                  <span
                    style={{
                      minWidth: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: "#dc2626",
                      display: "inline-block",
                    }}
                    title="Open tasks need attention"
                  />
                )}
                <span>{showOpenTasks ? "▲" : "▼"}</span>
              </span>
            </button>

            {showOpenTasks && (
  <div style={{ marginTop: 10 }}>
    {openTasks.length === 0 ? (
      <p style={{ color: "#64748b" }}>No open tasks.</p>
    ) : (
      openTasks.map((task) => (
        <div
          key={task.id}
          style={{
            border: task.urgent ? "1px solid #dc2626" : "1px solid #cbd5e1",
            borderRadius: 8,
            padding: 10,
            marginBottom: 8,
            background: task.urgent ? "#fef2f2" : "#f8fafc",
          }}
        >
          <div style={{ fontWeight: 800 }}>{task.title}</div>

          {task.details ? (
            <div style={{ marginTop: 4, color: "#334155" }}>
              {task.details}
            </div>
          ) : null}

          {/* ✅ ADD IMAGE BLOCK RIGHT HERE */}
          {Array.isArray(task.files) && task.files.length > 0 && (
            <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
              {task.files.map((file, i) => (
                <a
                  key={i}
                  href={file.fileURL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={file.fileURL}
                    alt=""
                    style={{
                      width: 60,
                      height: 60,
                      objectFit: "cover",
                      borderRadius: 6,
                      border: "1px solid #cbd5e1"
                    }}
                  />
                </a>
              ))}
            </div>
          )}

          <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>
            Created by {task.createdByName || "User"}
            <br />
Assigned to: {getTaskAssignedLabel(task)}
          </div>

          <div
  style={{
    marginTop: 8,
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    alignItems: "center",
  }}
>
  <button
    onClick={() => completeTask(task)}
    style={greenButtonStyle}
  >
    Mark Completed
  </button>

  <button
    onClick={() => deleteTask(task)}
    style={redButtonStyle}
  >
    Delete Task
  </button>

  <button
    onClick={() =>
      task.urgent ? markTaskNonUrgent(task) : markTaskUrgent(task)
    }
    style={{
      ...tinyButtonStyle,
      marginLeft: "auto",
      background: task.urgent ? "#f8fafc" : "#fee2e2",
      border: task.urgent ? "1px solid #94a3b8" : "1px solid #dc2626",
      color: task.urgent ? "#0f172a" : "#991b1b",
    }}
  >
    {task.urgent ? "Non Urgent" : "Urgent"}
  </button>
</div>
        </div>
      ))
    )}
  </div>
)}

          <div style={{ ...boxStyle, padding: 10 }}>
            <button
              type="button"
              onClick={() => setShowCompletedTasks((p) => !p)}
              style={sectionToggleStyle(showCompletedTasks)}
            >
              <span>
                {showCompletedTasks ? "Hide Completed Tasks" : "Show Completed Tasks"} ({completedTasks.length})
              </span>
              <span>{showCompletedTasks ? "▲" : "▼"}</span>
            </button>

            {showCompletedTasks && (
              <div style={{ marginTop: 10 }}>
                {completedTasks.length > 0 && (
  <button
    type="button"
    onClick={deleteAllCompletedTasks}
    style={{
      ...redButtonStyle,
      width: "100%",
      marginBottom: 10,
      background: "#111827",
      border: "1px solid #111827",
      color: "#fff",
    }}
  >
    Delete All Completed
  </button>
)}
                {completedTasks.length === 0 ? (
                  <p style={{ color: "#64748b" }}>No completed tasks yet.</p>
                ) : (
                  completedTasks.map((task) => (
                    <div
                      key={task.id}
                      style={{
                        border: "1px solid #bbf7d0",
                        borderRadius: 8,
                        padding: 10,
                        marginBottom: 8,
                        background: "#f0fdf4",
                      }}
                    >
                      <div style={{ fontWeight: 800 }}>{task.title}</div>
                      {task.details ? (
                        <div style={{ marginTop: 4, color: "#334155" }}>
                          {task.details}
                        </div>
                      ) : null}
                      <div style={{ fontSize: 12, color: "#166534", marginTop: 6 }}>
                        Completed by {task.completedByName || "User"}
                        <br />
Assigned to: {getTaskAssignedLabel(task)}
                      </div>
                      <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
  <button
    onClick={() => reopenTask(task)}
    style={redButtonStyle}
  >
    Re-Open Task
  </button>

  <button
    onClick={() => deleteTask(task)}
    style={{
      ...redButtonStyle,
      background: "#111827",
      border: "1px solid #111827",
      color: "#fff",
    }}
  >
    Delete Task
  </button>
</div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          <div style={{ ...boxStyle, padding: 10 }}>
  <button
    type="button"
    onClick={() => setShowDirectMessage((p) => !p)}
    style={sectionToggleStyle(
      showDirectMessage,
      conversations.some((c) => c.unread)
    )}
  >
    <span>
      {showDirectMessage ? "Hide Direct / Group Message" : "Show Direct / Group Message"}
    </span>
    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {conversations.some((c) => c.unread) && (
        <span
          style={{
            minWidth: 10,
            height: 10,
            borderRadius: "50%",
            background: "#dc2626",
            display: "inline-block",
          }}
          title="Unread messages"
        />
      )}
      <span>{showDirectMessage ? "▲" : "▼"}</span>
    </span>
  </button>
  <button
  type="button"
  onClick={() => setShowNewGroupPicker((p) => !p)}
  style={{
    ...buttonStyle,
    width: "100%",
    marginBottom: 8,
    background: "#dcfce7",
    border: "1px solid #16a34a",
    color: "#000",
  }}
>
  + New Group Message
</button>

{showNewGroupPicker && (
  <div
    style={{
      border: "1px solid #cbd5e1",
      borderRadius: 8,
      padding: 10,
      marginBottom: 10,
      background: "#fff",
    }}
  >
    <div style={{ fontWeight: 700, marginBottom: 6 }}>
      Select Users:
    </div>

    {availableRecipients.map((user) => (
     <label
     key={user.id}
     style={{
       display: "block",
       marginBottom: 6,
       cursor: "pointer",
       color: "#000",
     }}
   >
        <input
          type="checkbox"
          checked={groupDraftRecipients.includes(user.id)}
          onChange={() => toggleGroupDraftRecipient(user.id)}
          style={{ marginRight: 8 }}
        />
        {user.name}
      </label>
    ))}

    <button
      onClick={startGroupConversation}
      style={{
        ...greenButtonStyle,
        marginTop: 8,
        width: "100%",
      }}
    >
      Start Group Conversation
    </button>
  </div>
)}

{showDirectMessage && (
  <div style={{ marginTop: 10 }}>
    <div style={{ fontWeight: 700, marginBottom: 8, color: "#000" }}>
      Open a conversation or start a direct message:
    </div>

    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {conversations.map((conv) => (
        <div
          key={conv.key}
          style={{
            display: "flex",
            gap: 6,
            alignItems: "center",
          }}
        >
          <button
            type="button"
            onClick={() => {
              setActiveConversationKey(conv.key);
              setReplyingToMessage(null);
              setConversationSeenMap((prev) => ({
                ...prev,
                [conv.key]: new Date(),
              }));
            }}
            style={{
              ...buttonStyle,
              flex: 1,
              textAlign: "left",
              background: activeConversationKey === conv.key ? "#bfdbfe" : "#e0e7ff",
              border: conv.unread ? "1px solid #dc2626" : "1px solid #1d4ed8",
              color: "#000",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span style={{ color: "#000" }}>{conv.label}</span>

            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {conv.unread && (
                <span
                  style={{
                    minWidth: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: "#dc2626",
                    display: "inline-block",
                  }}
                />
              )}
              <span style={{ fontSize: 12, color: "#475569" }}>
                {conv.latestAt ? conv.latestAt.toLocaleString() : ""}
              </span>
            </span>
          </button>

          {conv.key !== "team_all" && (
            <button
              type="button"
              onClick={() => hideConversation(conv.key)}
              style={{
                ...tinyButtonStyle,
                whiteSpace: "nowrap",
              }}
              title="Hide this conversation"
            >
              Hide
            </button>
          )}
        </div>
      ))}
    </div>

    {hiddenConversationsList.length > 0 && (
      <div style={{ marginTop: 12 }}>
        <div style={{ fontWeight: 700, marginBottom: 6, color: "#000" }}>
          Hidden Conversations
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {hiddenConversationsList.map((conv) => (
            <div
              key={conv.key}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                border: "1px solid #cbd5e1",
                borderRadius: 8,
                padding: "6px 8px",
                background: "#fff",
              }}
            >
              <span style={{ fontSize: 13, color: "#000" }}>{conv.label}</span>

              <button
                type="button"
                onClick={() => unhideConversation(conv.key)}
                style={tinyButtonStyle}
              >
                Show Again
              </button>
            </div>
          ))}
        </div>
      </div>
    )}

    <div style={{ fontSize: 12, color: "#64748b", marginTop: 8 }}>
      Clicking a user or group shows only that conversation in Team Activity.
    </div>
  </div>
)}
</div>

        </div>
      </div>
    </div>
    </div>
  );
};

export default TeamMessaging;